import {Dialog, AvatarMultiChoice, GetItemImage} from 'platform/components';

import {useCallback, useState} from 'react';

import {useLazyGetUserAvatarUrlQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {Nullish} from 'shared/web';

type UserOption = {
  value: string;
  label: string;
};

interface AddMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userIds: string[]) => void;
  userOptions: UserOption[] | Nullish;
  isLoading?: boolean;
}

export function AddMemberDialog(props: AddMemberDialogProps) {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [getUserAvatar] = useLazyGetUserAvatarUrlQuery();

  const getUserAvatarUrl: GetItemImage<string> = useCallback(
    async (item) => {
      if (!item.value) {
        return null;
      }
      const imageUrl = await getUserAvatar({userId: item.value!, height: 120, width: 120}, true)
        .unwrap()
        .catch(handleApiError);

      return imageUrl ?? null;
    },
    [getUserAvatar]
  );

  const handleSelect = (value: string[] | null) => {
    setSelectedUserIds(value ?? []);
  };

  const handleSave = () => {
    props.onSave(selectedUserIds);
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="small"
      scrollBehavior="outside"
      title={i18n.t('page.settings.actions.addTeamMember')}
      buttons={[
        {
          variant: 'secondary',
          onClick: props.onClose,
          title: i18n.t('general.actions.discard'),
          'data-testid': testIds.settings.teamManagementDetail('addMemberDialog-discard'),
        },
        {
          variant: 'primary',
          onClick: handleSave,
          title: i18n.t('general.actions.save'),
          isLoading: props.isLoading,
          isDisabled: selectedUserIds.length === 0,
          'data-testid': testIds.settings.teamManagementDetail('addMemberDialog-save'),
        },
      ]}
      data-testid={testIds.settings.teamManagementDetail('addMemberDialog')}
    >
      <AvatarMultiChoice
        value={selectedUserIds}
        options={props.userOptions ?? []}
        onChange={handleSelect}
        hasOptionCheckbox
        placeholder={i18n.t('general.labels.selectUsers')}
        getItemImage={getUserAvatarUrl}
        closeMenuOnSelect={false}
        data-testid={testIds.settings.teamManagementDetail('addMemberDialog-userSelect')}
      />
    </Dialog>
  );
}
